import type {
  GpuCapability,
  InferenceMessage,
  ModelDefinition,
  ModelLoadState,
  ResponseMode,
} from "../types";

interface GenerationResult {
  text: string;
  tokens: number;
  tokensPerSecond: number;
  rag?: {
    used: boolean;
    sources: Array<{ kind: string; title: string; url: string }>;
  };
}

interface InferResponse {
  text?: string;
  tokens?: number;
  tokensPerSecond?: number;
  error?: { code?: string; message?: string };
  rag?: GenerationResult["rag"];
}

export class InferenceClient {
  private activeAbort: AbortController | null = null;

  async checkGpu(): Promise<GpuCapability> {
    return {
      available: true,
      adapterName: "Nexus AI",
      architecture: "server",
      shaderF16: true,
    };
  }

  async load(model: ModelDefinition, onState: (state: ModelLoadState) => void): Promise<void> {
    onState({ status: "checking" });
    const response = await fetch("/api/model/status", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    const body = await response.json().catch(() => ({})) as {
      configured?: boolean;
      error?: { message?: string };
    };
    if (!response.ok) {
      throw new Error(body.error?.message || "AIの状態を確認できませんでした。");
    }
    if (!body.configured) {
      throw new Error("AIを準備できていません。しばらくしてからもう一度お試しください。");
    }
    onState({ status: "ready", modelId: model.id });
  }

  async prepare(
    messages: InferenceMessage[],
    _model: ModelDefinition,
    _options: {
      mode: ResponseMode;
      maxNewTokens: number;
      contextTokens: number;
    },
  ): Promise<{ messages: InferenceMessage[]; promptTokens: number }> {
    return { messages, promptTokens: estimateTokens(messages.map((message) => message.content).join("\n")) };
  }

  async generate(
    messages: InferenceMessage[],
    model: ModelDefinition,
    options: {
      mode: ResponseMode;
      temperature: number;
      maxNewTokens: number;
      contextTokens: number;
      ragEnabled: boolean;
      onToken: (output: string, tokensPerSecond: number) => void;
      signal?: AbortSignal;
    },
  ): Promise<GenerationResult> {
    if (options.signal?.aborted) {
      throw new DOMException("生成を停止しました。", "AbortError");
    }
    const controller = new AbortController();
    this.activeAbort = controller;
    const abort = (): void => controller.abort();
    options.signal?.addEventListener("abort", abort, { once: true });
    const startedAt = performance.now();
    try {
      const requestMessages = normalizeMessages(messages);
      const htmlTagPipeline = shouldUseHtmlTagPipeline(requestMessages);
      if (htmlTagPipeline) {
        const result = await generateHtmlByTagPipeline({
          modelId: model.id,
          messages: requestMessages,
          mode: options.mode,
          temperature: Math.min(options.temperature, 0.2),
          maxNewTokens: Math.max(options.maxNewTokens, 768),
          contextTokens: options.contextTokens,
          signal: controller.signal,
        });
        const elapsed = Math.max(0.001, (performance.now() - startedAt) / 1000);
        const tokensPerSecond = result.tokens / elapsed;
        options.onToken(result.text, tokensPerSecond);
        return { ...result, tokensPerSecond };
      }
      const codeQualityLoop = shouldUseCodeQualityLoop(requestMessages);
      let result = await requestInference({
        modelId: model.id,
        messages: requestMessages,
        mode: options.mode,
        temperature: options.temperature,
        maxNewTokens: codeQualityLoop ? Math.max(options.maxNewTokens, 1024) : options.maxNewTokens,
        contextTokens: options.contextTokens,
        ragEnabled: options.ragEnabled,
        signal: controller.signal,
      });
      let totalTokens = result.tokens;
      let rag = result.rag;
      if (codeQualityLoop) {
        result = await requestInference({
          modelId: model.id,
          messages: buildCodeRefinementMessages(requestMessages, result.text, false),
          mode: options.mode,
          temperature: Math.min(options.temperature, 0.2),
          maxNewTokens: Math.max(options.maxNewTokens, 1536),
          contextTokens: options.contextTokens,
          ragEnabled: false,
          signal: controller.signal,
        });
        totalTokens += result.tokens;
      }
      if (shouldRepairGeneratedAnswer(requestMessages, result.text)) {
        result = await requestInference({
          modelId: model.id,
          messages: buildCodeRefinementMessages(requestMessages, result.text, true),
          mode: options.mode,
          temperature: 0,
          maxNewTokens: Math.max(options.maxNewTokens, 1536),
          contextTokens: options.contextTokens,
          ragEnabled: false,
          signal: controller.signal,
        });
        totalTokens += result.tokens;
      }
      const elapsed = Math.max(0.001, (performance.now() - startedAt) / 1000);
      const tokensPerSecond = totalTokens / elapsed;
      options.onToken(result.text, tokensPerSecond);
      return { text: result.text, tokens: totalTokens, tokensPerSecond, rag };
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new DOMException("生成を停止しました。", "AbortError");
      }
      throw error;
    } finally {
      options.signal?.removeEventListener("abort", abort);
      if (this.activeAbort === controller) this.activeAbort = null;
    }
  }

  interrupt(): void {
    this.activeAbort?.abort();
  }

  destroy(): void {
    this.interrupt();
  }
}

function estimateTokens(text: string): number {
  return Math.max(1, Math.ceil(text.length / 4));
}

function normalizeMessages(messages: InferenceMessage[]): InferenceMessage[] {
  const normalized = messages
    .map((message) => ({
      role: message.role,
      content: stripUnsafeControlCharacters(message.content).trim().slice(0, 80_000),
    }))
    .filter((message) => message.content.length > 0)
    .slice(-48);
  if (normalized.length === 0) {
    throw new Error("送信できるメッセージがありません。入力内容を確認してください。");
  }
  return normalized;
}

function stripUnsafeControlCharacters(value: string): string {
  return value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/gu, "");
}

async function requestInference(input: {
  modelId: string;
  messages: InferenceMessage[];
  mode: ResponseMode;
  temperature: number;
  maxNewTokens: number;
  contextTokens: number;
  ragEnabled: boolean;
  signal: AbortSignal;
}): Promise<GenerationResult> {
  const response = await fetch("/api/infer", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({
      modelId: input.modelId,
      messages: normalizeMessages(input.messages),
      mode: input.mode,
      temperature: input.temperature,
      maxNewTokens: Math.min(4096, input.maxNewTokens),
      contextTokens: input.contextTokens,
      rag: { enabled: input.ragEnabled },
    }),
    signal: input.signal,
  });
  const body = await response.json().catch(() => ({})) as InferResponse;
  if (!response.ok || typeof body.text !== "string") {
    throw new Error(body.error?.message || "AIで生成できませんでした。");
  }
  const tokens = body.tokens ?? estimateTokens(body.text);
  return {
    text: body.text,
    tokens,
    tokensPerSecond: body.tokensPerSecond ?? tokens,
    rag: body.rag,
  };
}

export function shouldUseCodeQualityLoop(messages: InferenceMessage[]): boolean {
  const text = latestUserContent(messages).toLowerCase();
  return /html|css|javascript|typescript|react|tsx|jsx|python|コード|実装|プログラム|アプリ|サイト|ページ|component|function|class|api/u.test(text);
}

export function shouldUseHtmlTagPipeline(messages: InferenceMessage[]): boolean {
  const text = latestUserContent(messages).toLowerCase();
  return /html|<!doctype|単体ページ|webページ|ウェブページ|サイト|landing page|lp|divタグ|scriptタグ|css/u.test(text)
    && !/react|tsx|jsx|vue|svelte|python|typescript|node\.?js|api/u.test(text);
}

async function generateHtmlByTagPipeline(input: {
  modelId: string;
  messages: InferenceMessage[];
  mode: ResponseMode;
  temperature: number;
  maxNewTokens: number;
  contextTokens: number;
  signal: AbortSignal;
}): Promise<GenerationResult> {
  const request = latestUserContent(input.messages);
  const base = {
    modelId: input.modelId,
    mode: input.mode,
    temperature: input.temperature,
    contextTokens: input.contextTokens,
    ragEnabled: false,
    signal: input.signal,
  };
  const title = deriveHtmlTitle(request);
  const calls = await Promise.all([
    requestInference({
      ...base,
      maxNewTokens: input.maxNewTokens,
      messages: tagPrompt(request, "divタグ① .hero", "ページ上部の見出し、短い説明、主要ボタンだけのHTML断片を返してください。div/section/h1/p/a/buttonは可。html/head/body/style/scriptは禁止。"),
    }),
    requestInference({
      ...base,
      maxNewTokens: input.maxNewTokens,
      messages: tagPrompt(request, "divタグ② .content", "本文エリアのHTML断片を返してください。カード、箇条書き、説明ブロックを含めてください。html/head/body/style/scriptは禁止。"),
    }),
    requestInference({
      ...base,
      maxNewTokens: Math.min(input.maxNewTokens, 512),
      messages: tagPrompt(request, "divタグ③ .footer", "ページ下部の短いフッターHTML断片だけを返してください。html/head/body/style/scriptは禁止。"),
    }),
    requestInference({
      ...base,
      maxNewTokens: input.maxNewTokens,
      messages: tagPrompt(request, "styleタグ", "このページ用CSSだけを返してください。説明、Markdown、styleタグは禁止。レスポンシブで見やすいデザインにしてください。"),
    }),
    requestInference({
      ...base,
      maxNewTokens: Math.min(input.maxNewTokens, 512),
      messages: tagPrompt(request, "scriptタグ", "このページに必要なJavaScriptだけを返してください。不要なら console.log(\"ready\"); だけ。説明、Markdown、scriptタグは禁止。"),
    }),
  ]);
  const html = assembleStandaloneHtmlFromParts({
    title,
    request,
    hero: calls[0].text,
    content: calls[1].text,
    footer: calls[2].text,
    css: calls[3].text,
    script: calls[4].text,
  });
  return {
    text: html,
    tokens: calls.reduce((total, call) => total + call.tokens, estimateTokens(html)),
    tokensPerSecond: 0,
  };
}

function tagPrompt(request: string, tagName: string, instruction: string): InferenceMessage[] {
  return normalizeMessages([
    {
      role: "system",
      content: [
        `あなたは${tagName}専用の部品生成AIです。`,
        "担当部品以外は絶対に出力しないでください。",
        "謝罪、解説、Markdownコードフェンス、同じ文の繰り返しは禁止です。",
      ].join("\n"),
    },
    {
      role: "user",
      content: `元の依頼:\n${request}\n\n担当: ${tagName}\n指示: ${instruction}`,
    },
  ]);
}

export function assembleStandaloneHtmlFromParts(input: {
  title: string;
  request: string;
  hero: string;
  content: string;
  footer: string;
  css: string;
  script: string;
}): string {
  const hero = sanitizeHtmlFragment(input.hero) || `<h1>${escapeHtml(input.title)}</h1><p>${escapeHtml(input.request)}</p>`;
  const content = sanitizeHtmlFragment(input.content) || "<p>内容をここに表示します。</p>";
  const footer = sanitizeHtmlFragment(input.footer) || "<p>&copy; 2026</p>";
  const css = sanitizeCss(input.css);
  const script = sanitizeJavaScript(input.script);
  return [
    "<!DOCTYPE html>",
    "<html lang=\"ja\">",
    "<head>",
    "  <meta charset=\"UTF-8\" />",
    "  <meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\" />",
    `  <title>${escapeHtml(input.title)}</title>`,
    "  <style>",
    indent(css || defaultHtmlCss()),
    "  </style>",
    "</head>",
    "<body>",
    "  <main class=\"page-shell\">",
    "    <section class=\"hero\">",
    indent(hero, 6),
    "    </section>",
    "    <section class=\"content\">",
    indent(content, 6),
    "    </section>",
    "  </main>",
    "  <footer class=\"site-footer\">",
    indent(footer, 4),
    "  </footer>",
    "  <script>",
    indent(script || "console.log(\"ready\");", 4),
    "  </script>",
    "</body>",
    "</html>",
  ].join("\n");
}

export function shouldRepairGeneratedAnswer(messages: InferenceMessage[], answer: string): boolean {
  const user = latestUserContent(messages).toLowerCase();
  const wantsHtml = /html|<!doctype|サイト|ページ/u.test(user);
  const wantsCode = shouldUseCodeQualityLoop(messages);
  const repeated = hasRepeatedParagraph(answer) || hasRepeatedSentence(answer);
  const apologeticLoop = /(申し訳ありません[、。]){2,}|あなたが提供したコードは/u.test(answer);
  const missingCode = wantsCode && !/[`]{3}|<!doctype|<html[\s>]|function\s+\w+|const\s+\w+|class\s+\w+|def\s+\w+/iu.test(answer);
  const brokenHtml = wantsHtml && !/<!doctype html>|<html[\s>][\s\S]*<\/html>/iu.test(answer);
  return repeated || apologeticLoop || missingCode || brokenHtml;
}

function buildCodeRefinementMessages(
  originalMessages: InferenceMessage[],
  draft: string,
  forceRepair: boolean,
): InferenceMessage[] {
  const original = latestUserContent(originalMessages);
  const instruction = forceRepair
    ? "前回の回答は不十分です。繰り返し、謝罪、説明の水増しをすべて削除し、ユーザーがそのまま使える完成版だけを返してください。HTMLなら <!DOCTYPE html> から </html> までの単一ファイルを返してください。"
    : "上の下書きを自己レビューして、実用できる完成版に直してください。前置き、謝罪、同じ説明の繰り返しは禁止です。コード依頼なら完成コードだけを返してください。";
  return normalizeMessages([
    {
      role: "system",
      content: [
        "あなたは厳格なコードレビュー兼修正AIです。",
        "出力は完成版だけ。謝罪、言い訳、自己言及、重複説明は禁止。",
        "コードは構文が壊れないようにし、HTML依頼なら単独で保存して動く完全なHTMLにしてください。",
      ].join("\n"),
    },
    { role: "user", content: `元の依頼:\n${original}` },
    { role: "assistant", content: draft.slice(0, 60_000) },
    { role: "user", content: instruction },
  ]);
}

function latestUserContent(messages: InferenceMessage[]): string {
  return [...messages].reverse().find((message) => message.role === "user")?.content ?? "";
}

function hasRepeatedParagraph(text: string): boolean {
  const paragraphs = text
    .split(/\n{2,}/u)
    .map((part) => part.replace(/\s+/gu, " ").trim())
    .filter((part) => part.length >= 40);
  return new Set(paragraphs).size < paragraphs.length;
}

function hasRepeatedSentence(text: string): boolean {
  const sentences = text
    .split(/[。.!?！？]\s*/u)
    .map((part) => part.replace(/\s+/gu, " ").trim())
    .filter((part) => part.length >= 28);
  const counts = new Map<string, number>();
  for (const sentence of sentences) {
    const key = sentence.slice(0, 90);
    const next = (counts.get(key) ?? 0) + 1;
    if (next >= 2) return true;
    counts.set(key, next);
  }
  return false;
}

function stripCodeFence(value: string): string {
  const text = stripUnsafeControlCharacters(value).trim();
  const fenced = text.match(/```(?:html|css|javascript|js)?\s*([\s\S]*?)```/iu);
  return (fenced?.[1] ?? text).trim();
}

function sanitizeHtmlFragment(value: string): string {
  return stripCodeFence(value)
    .replace(/<!doctype[^>]*>/giu, "")
    .replace(/<\/?(?:html|head|body|style|script|meta|title)[^>]*>/giu, "")
    .replace(/^\s*(?:以下|こちら|説明|解説)[:：].*$/gimu, "")
    .trim()
    .slice(0, 30_000);
}

function sanitizeCss(value: string): string {
  const css = stripCodeFence(value)
    .replace(/<\/?style[^>]*>/giu, "")
    .replace(/<\/?script[^>]*>/giu, "")
    .trim();
  if (!/[{}:;]/u.test(css)) return defaultHtmlCss();
  return css.slice(0, 30_000);
}

function sanitizeJavaScript(value: string): string {
  const script = stripCodeFence(value)
    .replace(/<\/?script[^>]*>/giu, "")
    .replace(/<\/?style[^>]*>/giu, "")
    .trim();
  if (!script || /申し訳|以下は|```/u.test(script)) return "console.log(\"ready\");";
  return script.slice(0, 20_000);
}

function deriveHtmlTitle(request: string): string {
  const cleaned = request
    .replace(/html|単体|ページ|作って|作成|コード|完成|だけ|ください/giu, " ")
    .replace(/\s+/gu, " ")
    .trim();
  return (cleaned || "AI Generated Page").slice(0, 60);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/gu, "&amp;")
    .replace(/</gu, "&lt;")
    .replace(/>/gu, "&gt;")
    .replace(/"/gu, "&quot;");
}

function indent(value: string, spaces = 4): string {
  const prefix = " ".repeat(spaces);
  return value.split(/\r?\n/u).map((line) => `${prefix}${line}`.trimEnd()).join("\n");
}

function defaultHtmlCss(): string {
  return [
    ":root { color-scheme: dark; font-family: system-ui, sans-serif; }",
    "body { margin: 0; min-height: 100vh; background: #0f172a; color: #f8fafc; }",
    ".page-shell { width: min(960px, calc(100% - 32px)); margin: 0 auto; padding: 72px 0 32px; }",
    ".hero, .content, .site-footer { border: 1px solid rgba(255,255,255,.12); border-radius: 24px; padding: 28px; background: rgba(255,255,255,.06); }",
    ".content, .site-footer { margin-top: 20px; }",
    "button, a { color: inherit; }",
  ].join("\n");
}
